import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { baseURL } from '../apiConfig';
import axios from 'axios';
import LiveRateStrip from './LiveRateStrip';
import './Collection.css'; 
import imageCompression from 'browser-image-compression';


const Collection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // 👥 User Role State
  const [userRole, setUserRole] = useState(() => {
    const role = sessionStorage.getItem('userRole') || 
                 localStorage.getItem('userRole') || 
                 localStorage.getItem('role') || 
                 'Guest';
    const normalizedRole = role.trim().toLowerCase();
    return (normalizedRole === 'guest' || normalizedRole === 'customer') ? 'Guest' : role;
  });

  // Filters State
  const [metal, setMetal] = useState('all');
  const [purity, setPurity] = useState('all');
  const [group, setGroup] = useState('all');
  const [sortBy, setSortBy] = useState('latest');

  // Dynamic Master Lists
  const [masterMetals, setMasterMetals] = useState([]);
  const [masterPurities, setMasterPurities] = useState([]);
  const [masterGroups, setMasterGroups] = useState([]);
  const [activeImages, setActiveImages] = useState({});

  // Management Upload Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Photo Zoom & Lightbox State (Accessible to any user / guest)
  const [zoomModal, setZoomModal] = useState({
    isOpen: false,
    images: [],
    currentIndex: 0,
    tagId: '',
    itemName: ''
  });

  // Customer Enquiry Modal States
  const [isEnquiryModalOpen, setIsEnquiryModalOpen] = useState(false);
  const [enquiryItem, setEnquiryItem] = useState({ tagId: '', itemName: '' });
  const [enquiryForm, setEnquiryForm] = useState({
    customerName: '',
    customerMobile: '',
    customerRemarks: ''
  });
  const [submittingEnquiry, setSubmittingEnquiry] = useState(false);

  // Offer Banner States
  const [offerText, setOfferText] = useState('');
  const [isOfferActive, setIsOfferActive] = useState(false);
  const [isEditingOffer, setIsEditingOffer] = useState(false); 
  const [inputOfferText, setInputOfferText] = useState('');

  // 📈 Live Rate Strip Visibility & 10s Auto-Hide State
  const [showLiveRates, setShowLiveRates] = useState(false);
  const [rateCountdown, setRateCountdown] = useState(10);
  const isRatesEditingRef = useRef(false);
  const autoHideTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  const clearRateTimers = useCallback(() => {
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const handleCloseLiveRates = useCallback(() => {
    clearRateTimers();
    setShowLiveRates(false);
  }, [clearRateTimers]);

  const startAutoCloseTimer = useCallback((seconds = 10) => {
    clearRateTimers();
    if (isRatesEditingRef.current) return;

    setRateCountdown(seconds);
    let remaining = seconds;

    countdownIntervalRef.current = setInterval(() => {
      remaining -= 1;
      setRateCountdown(remaining);
      if (remaining <= 0) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
        setShowLiveRates(false);
      }
    }, 1000);

    autoHideTimerRef.current = setTimeout(() => {
      setShowLiveRates(false);
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }, seconds * 1000);
  }, [clearRateTimers]);

  const openLiveRates = useCallback(() => {
    setShowLiveRates(true);
    startAutoCloseTimer(10);
  }, [startAutoCloseTimer]);

  const handleRatesEditingChange = useCallback((isEditing) => {
    isRatesEditingRef.current = isEditing;
    if (isEditing) {
      clearRateTimers();
    } else {
      startAutoCloseTimer(10);
    }
  }, [clearRateTimers, startAutoCloseTimer]);

  // 🔔 Listen to Navbar Live Rate button event or router state
  useEffect(() => {
    const handleToggleEvent = () => {
      openLiveRates();
    };

    window.addEventListener('toggle-live-rates', handleToggleEvent);

    if (location.state && location.state.triggerLiveRates) {
      openLiveRates();
    }

    return () => {
      window.removeEventListener('toggle-live-rates', handleToggleEvent);
      clearRateTimers();
    };
  }, [location.state, openLiveRates, clearRateTimers]);

  // 🔑 Helper Function: Guest ko chhor ke sab ko management access
  const isStaffUser = (role) => {
    if (!role) return false;
    const normalized = role.toString().trim().toLowerCase();
    return normalized !== 'guest' && normalized !== 'customer';
  };

  // 🔄 Load Dropdowns
  const loadInitialFilterData = async () => {
    try {
      const response = await axios.get(`${baseURL}/api/master/dropdowns/`);
      if (response.data.success) {
        if (response.data.metals) setMasterMetals(response.data.metals);
        if (response.data.purities) setMasterPurities(response.data.purities);
        if (response.data.groups) {
          const uniqueG = Array.from(new Set(response.data.groups.map(g => (g || '').trim()))).filter(Boolean);
          setMasterGroups(uniqueG);
        }
      }
    } catch (error) {
      console.error("Master Table fetch error:", error);
    }
  };

  const handleMetalChange = async (selectedMetal) => {
    setMetal(selectedMetal);
    setPurity('all');
    setGroup('all');

    try {
      const url = selectedMetal === 'all' 
        ? `${baseURL}/api/master/dropdowns/` 
        : `${baseURL}/api/master/dropdowns/?metal=${selectedMetal}`;
        
      const response = await axios.get(url);
      if (response.data.success) {
        if (response.data.purities) setMasterPurities(response.data.purities);
        if (response.data.groups) {
          const uniqueG = Array.from(new Set(response.data.groups.map(g => (g || '').trim()))).filter(Boolean);
          setMasterGroups(uniqueG);
        }
      }
    } catch (error) {
      console.error("Dependent dropdowns error:", error);
    }
  };

  const fetchOfferSettings = async () => {
    try {
      const response = await axios.get(`${baseURL}/api/get-active-offer/`);
      if (response.data.success) {
        setOfferText(response.data.offer_text);
        setIsOfferActive(response.data.is_active);
        setInputOfferText(response.data.offer_text);
      }
    } catch (error) {
      console.error("Offer settings fetch error:", error);
    }
  };

  const handleUpdateOffer = async () => {
    try {
      const response = await axios.post(`${baseURL}/api/update-offer/`, {
        offer_text: inputOfferText,
        is_active: isOfferActive
      });
      if (response.data.success) {
        setOfferText(inputOfferText);
        setIsEditingOffer(false); 
        alert(response.data.message);
      }
    } catch (error) {
      console.error("Offer save error:", error);
      alert("Offer details save karne me kuch problem hai !");
    }
  };

  // 📦 Fetch Stock Items
  const fetchStock = useCallback(async () => {
    setLoading(true);
    try {
      const isStaff = isStaffUser(userRole);
      let url = `${baseURL}/api/stock-report/?metal=${metal}&purity=${purity}`;
      if (group && group !== 'all') {
        url += `&group=${encodeURIComponent(group)}`;
      }
      // Guest view me sirf flag=1 wale items fetch honge
      if (!isStaff) {
        url += `&flag=1`;
      }
      const response = await axios.get(url);
      if (response.data.success) {
        setStockData(response.data.data);
      }
    } catch (error) {
      console.error("Data lane me error:", error);
    }
    setLoading(false);
  }, [metal, purity, group, userRole]);

  // Page Load Setup
  useEffect(() => {
    // SessionStorage pehle check karo, fir LocalStorage
    const role = sessionStorage.getItem('userRole') || 
                 localStorage.getItem('userRole') || 
                 localStorage.getItem('role') || 
                 'Guest';
                 
    const normalizedRole = role.trim().toLowerCase();
    
    if (normalizedRole === 'guest' || normalizedRole === 'customer') {
      setUserRole('Guest');
    } else {
      setUserRole(role);
    }

    loadInitialFilterData();
    fetchOfferSettings();
  }, []);

  // Fetch Stock jab filter ya page load ho
  useEffect(() => {
    fetchStock();
  }, [fetchStock]);

  // 🚩 Live Flag Update Handler (Admin Checkbox Toggle)
  const handleToggleFlag = async (tagId, isChecked) => {
    const newFlag = isChecked ? '1' : '0';

    // Optimistic UI update
    setStockData(prev => prev.map(item => {
      if (item.tagId === tagId) {
        return { ...item, flag: newFlag };
      }
      return item;
    }));

    // Live table stock_for_sale Flag column update via API
    try {
      const response = await axios.post(`${baseURL}/api/update-stock-flag/`, {
        tag_id: tagId,
        flag: newFlag
      });

      if (!response.data.success) {
        // Rollback on failure
        setStockData(prev => prev.map(item => {
          if (item.tagId === tagId) {
            return { ...item, flag: isChecked ? '0' : '1' };
          }
          return item;
        }));
        alert("Flag update nahi ho saka: " + (response.data.message || 'Error'));
      }
    } catch (error) {
      console.error("Flag update error:", error);
      // Rollback on error
      setStockData(prev => prev.map(item => {
        if (item.tagId === tagId) {
          return { ...item, flag: isChecked ? '0' : '1' };
        }
        return item;
      }));
      alert("Server error: Flag update failed!");
    }
  };

  // Sorting & Filtering (Same groups are always kept together)
  const getSortedData = () => {
    let sorted = [...stockData];
    // Guest view me sirf flag=1 wale cards display honge
    if (!isStaffUser(userRole)) {
      sorted = sorted.filter(item => String(item.flag) === '1');
    }
    // Group filter fallback
    if (group && group.toLowerCase() !== 'all') {
      sorted = sorted.filter(item => {
        const itemGrp = (item.group || item.itemGroup || '').trim().toLowerCase();
        return itemGrp === group.trim().toLowerCase();
      });
    }

    // 🌟 Group-First Sorting: Har group ke saare items ek sath display honge
    sorted.sort((a, b) => {
      const grpA = (a.group || a.itemGroup || 'Other Items').trim();
      const grpB = (b.group || b.itemGroup || 'Other Items').trim();

      // Pehle group name se compare karein taki same group sath rahe
      const cmpGroup = grpA.localeCompare(grpB, undefined, { sensitivity: 'base' });
      if (cmpGroup !== 0) {
        return cmpGroup;
      }

      // Group ke andar selected sortBy ke mutabik sort karein
      if (sortBy === 'lowToHigh') {
        return parseFloat(a.weight || 0) - parseFloat(b.weight || 0);
      } else if (sortBy === 'highToLow') {
        return parseFloat(b.weight || 0) - parseFloat(a.weight || 0);
      } else if (sortBy === 'oldest') {
        return (a.tagId || '').localeCompare(b.tagId || '');
      } else {
        // Default 'latest': us group ke andar latest tag ID pehle dikhegi
        return (b.tagId || '').localeCompare(a.tagId || '');
      }
    });

    return sorted;
  };

  // Management functions
  const openUploadModal = (tagId) => {
    setSelectedTag(tagId);
    setSelectedFiles([]);
    setIsModalOpen(true);
  };

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files));
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) {
      alert("Pehle koi photo to select kijiye !");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('tag_text', selectedTag);

    try {
      for (const file of selectedFiles) {
        const options = {
          maxSizeMB: 0.8,
          maxWidthOrHeight: 1200,
          useWebWorker: true,
        };
        try {
          const compressedFile = await imageCompression(file, options);
          formData.append('item_images', compressedFile, file.name);
        } catch (error) {
          console.error("Image compression error:", error);
          formData.append('item_images', file);
        }
      }


      const response = await axios.post(`${baseURL}/api/upload-stock-image/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        alert(response.data.message);
        setIsModalOpen(false);
        fetchStock();
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert("Error in photo uploading !");
    }
    setUploading(false);
  };

  const handleDeleteImage = async (tagId, imageUrl, e) => {
    e.stopPropagation(); 
    if (!window.confirm("Bhai, kya aap sach me is photo ko hatana chahte hain?")) return;

    try {
      const response = await axios.post(`${baseURL}/api/delete-stock-image/`, {
        tag_text: tagId,
        image_url: imageUrl
      });

      if (response.data.success) {
        alert(response.data.message);
        if (activeImages[tagId] === imageUrl) {
          setActiveImages(prev => {
            const newState = { ...prev };
            delete newState[tagId];
            return newState;
          });
        }
        fetchStock(); 
      }
    } catch (error) {
      console.error("Delete error:", error);
      alert("Photo delete nahi ho payi!");
    }
  };

  // Enquiry Functions
  const openEnquiryModal = (tagId, itemName) => {
    setEnquiryItem({ tagId, itemName });
    const savedName = localStorage.getItem('cachedCustomerName') || '';
    const savedMobile = localStorage.getItem('cachedCustomerMobile') || '';
    
    setEnquiryForm({
      customerName: savedName,
      customerMobile: savedMobile,
      customerRemarks: ''
    });
    setIsEnquiryModalOpen(true);
  };

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    if (!enquiryForm.customerName || !enquiryForm.customerMobile) {
      alert("Name aur Mobile Number bharna zaroori hai!");
      return;
    }

    setSubmittingEnquiry(true);
    try {
      const response = await axios.post(`${baseURL}/api/sales-order/save/`, {
        tag_id: enquiryItem.tagId,
        item_name: enquiryItem.itemName,
        customer_name: enquiryForm.customerName,
        customer_mobile: enquiryForm.customerMobile,
        customer_remarks: enquiryForm.customerRemarks
      });

      if (response.data.success) {
        localStorage.setItem('cachedCustomerName', enquiryForm.customerName);
        localStorage.setItem('cachedCustomerMobile', enquiryForm.customerMobile);
        alert(`🎉 Dhanyawad! Aapki Enquiry successfully save ho gayi hai.\nEnquiry Identity No (SlNo): ${response.data.slno}`);
        setIsEnquiryModalOpen(false);
      } else {
        alert("Enquiry save nahi ho saki: " + response.data.message);
      }
    } catch (error) {
      console.error("Enquiry API Error:", error);
      alert("Enquiry not submited !");
    }
    setSubmittingEnquiry(false);
  };

  // 🖼️ Helper: Extract all unique image URLs for an item
  const getItemImages = useCallback((item) => {
    if (!item) return [];
    const images = [];
    if (item.imageUrl && !images.includes(item.imageUrl)) {
      images.push(item.imageUrl);
    }
    if (item.angleUrls && Array.isArray(item.angleUrls)) {
      item.angleUrls.forEach(url => {
        if (url && !images.includes(url)) {
          images.push(url);
        }
      });
    }
    return images;
  }, []);

  // 🛡️ Track history state for physical / browser back button handling
  const isHistoryPushedRef = useRef(false);

  // 🔍 Lightbox Handlers (Universal - Works in any login/guest mode)
  const handleOpenZoom = (item, initialImage) => {
    const images = getItemImages(item);
    const list = images.length > 0 ? images : (initialImage ? [initialImage] : []);
    if (list.length === 0) return;

    const initialIndex = initialImage ? list.indexOf(initialImage) : 0;

    // 🛡️ Push a history state so device Back button closes modal instead of leaving page
    if (!isHistoryPushedRef.current) {
      window.history.pushState({ isLightboxOpen: true }, '');
      isHistoryPushedRef.current = true;
    }

    setZoomModal({
      isOpen: true,
      images: list,
      currentIndex: initialIndex !== -1 ? initialIndex : 0,
      tagId: item.tagId || '',
      itemName: item.itemName || ''
    });
  };

  const handleCloseZoom = useCallback(() => {
    // If closed manually (via 'x' or backdrop), pop the history entry we pushed
    if (isHistoryPushedRef.current) {
      isHistoryPushedRef.current = false;
      window.history.back();
    }
    setZoomModal(prev => ({ ...prev, isOpen: false }));
  }, []);

  // 📱 Device / Browser Back Button Popstate Listener
  useEffect(() => {
    const handlePopState = () => {
      if (zoomModal.isOpen) {
        // Back button pressed! Close preview modal only, don't leave page!
        isHistoryPushedRef.current = false;
        setZoomModal(prev => ({ ...prev, isOpen: false }));
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [zoomModal.isOpen]);

  // 📱 Mobile Touch Swipe Left / Right to Change Preview Photos
  const touchStartXRef = useRef(null);
  const touchStartYRef = useRef(null);
  const touchEndXRef = useRef(null);
  const touchEndYRef = useRef(null);

  const handleTouchStart = (e) => {
    if (e.touches && e.touches.length > 0) {
      touchStartXRef.current = e.touches[0].clientX;
      touchStartYRef.current = e.touches[0].clientY;
      touchEndXRef.current = e.touches[0].clientX;
      touchEndYRef.current = e.touches[0].clientY;
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches && e.touches.length > 0) {
      touchEndXRef.current = e.touches[0].clientX;
      touchEndYRef.current = e.touches[0].clientY;
    }
  };

  const handleTouchEnd = () => {
    if (touchStartXRef.current === null || touchEndXRef.current === null) return;
    const diffX = touchEndXRef.current - touchStartXRef.current;
    const diffY = touchEndYRef.current - touchStartYRef.current;

    // Minimum swipe threshold (40px) and horizontal movement must be dominant over vertical
    if (Math.abs(diffX) > 40 && Math.abs(diffX) > Math.abs(diffY)) {
      if (diffX < 0) {
        // Swiped Left -> Show NEXT image
        handleNextImage();
      } else {
        // Swiped Right -> Show PREVIOUS image
        handlePrevImage();
      }
    }

    touchStartXRef.current = null;
    touchStartYRef.current = null;
    touchEndXRef.current = null;
    touchEndYRef.current = null;
  };

  // 🔄 Loop Previous: 1/4 -> 4/4 (Left navigation)
  const handlePrevImage = useCallback((e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setZoomModal(prev => {
      if (!prev.isOpen || prev.images.length <= 1) return prev;
      const total = prev.images.length;
      const newIndex = (prev.currentIndex - 1 + total) % total;
      if (prev.tagId) {
        setActiveImages(act => ({ ...act, [prev.tagId]: prev.images[newIndex] }));
      }
      return { ...prev, currentIndex: newIndex };
    });
  }, []);

  // 🔄 Loop Next: 4/4 -> 1/4 (Right navigation)
  const handleNextImage = useCallback((e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setZoomModal(prev => {
      if (!prev.isOpen || prev.images.length <= 1) return prev;
      const total = prev.images.length;
      const newIndex = (prev.currentIndex + 1) % total;
      if (prev.tagId) {
        setActiveImages(act => ({ ...act, [prev.tagId]: prev.images[newIndex] }));
      }
      return { ...prev, currentIndex: newIndex };
    });
  }, []);

  const handleSelectZoomImage = useCallback((index, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setZoomModal(prev => {
      if (!prev.isOpen) return prev;
      if (prev.tagId && prev.images[index]) {
        setActiveImages(act => ({ ...act, [prev.tagId]: prev.images[index] }));
      }
      return { ...prev, currentIndex: index };
    });
  }, []);

  // ⌨️ Keyboard Navigation (Left/Right Arrow and Escape key) & Body Scroll Lock
  useEffect(() => {
    if (!zoomModal.isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevImage();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNextImage();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleCloseZoom();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [zoomModal.isOpen, handlePrevImage, handleNextImage, handleCloseZoom]);

  const finalSortedStock = getSortedData();

  // 🏷️ Group items by group name so items of the same group display together
  const groupedStock = finalSortedStock.reduce((acc, item) => {
    const grp = (item.group || item.itemGroup || '').trim() || 'Other Items';
    if (!acc[grp]) {
      acc[grp] = [];
    }
    acc[grp].push(item);
    return acc;
  }, {});

  // 📦 Helper to render individual product card
  const renderProductCard = (item) => {
    const allAvailableAngles = getItemImages(item);
    const currentMainImage = activeImages[item.tagId] || (allAvailableAngles.length > 0 ? allAvailableAngles[0] : item.imageUrl);

    return (
      <div className="product-card" key={item.tagId}>
        {/* 👑 Admin Flag Checkbox: Top Right of Card (Visible ONLY to Admin) */}
        {isStaffUser(userRole) && (
          <div 
            className={`card-flag-badge ${String(item.flag) === '1' ? 'active-flag' : ''}`}
            title={String(item.flag) === '1' ? 'Guest Display: ON (Click to Hide)' : 'Guest Display: OFF (Click to Show)'}
            onClick={(e) => e.stopPropagation()}
          >
            <label className="card-flag-label">
              <input 
                type="checkbox"
                className="card-flag-checkbox"
                checked={String(item.flag) === '1'}
                onChange={(e) => handleToggleFlag(item.tagId, e.target.checked)}
              />
              <span className="card-flag-text">
                {String(item.flag) === '1' ? 'Live' : 'Hidden'}
              </span>
            </label>
          </div>
        )}

        <div className="card-image-box" style={{ position: 'relative' }}>
          {currentMainImage ? (
            <>
              <img 
                src={currentMainImage} 
                alt={item.itemName} 
                className="product-image" 
                onClick={() => handleOpenZoom(item, currentMainImage)} 
                style={{ cursor: 'zoom-in' }} 
                title="Click to zoom photo and browse angles"
              />
              {/* 🔒 Delete Button: Only Non-Guest */}
              {isStaffUser(userRole) && (
                <button 
                  type="button"
                  className="main-image-delete-btn"
                  onClick={(e) => handleDeleteImage(item.tagId, currentMainImage, e)}
                  title="Delete main photo"
                >
                  ×
                </button>
              )}
            </>
          ) : (
            <div className="no-image-placeholder"><span>No Photo</span></div>
          )}
        </div>

        {/* Thumbnails */}
        {allAvailableAngles.length > 1 && (
          <div className="thumbnails-container">
            {allAvailableAngles.map((url, idx) => (
              <div 
                key={idx} 
                className={`thumb-box ${currentMainImage === url ? 'active-thumb' : ''}`}
                onMouseEnter={() => setActiveImages(prev => ({ ...prev, [item.tagId]: url }))}
                onClick={() => setActiveImages(prev => ({ ...prev, [item.tagId]: url }))}
                style={{ position: 'relative' }}
              >
                <img src={url} alt="angle" />
                {isStaffUser(userRole) && (
                  <button 
                    type="button"
                    className="thumb-delete-btn"
                    onClick={(e) => handleDeleteImage(item.tagId, url, e)}
                    title="Delete photo"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Card Details */}
        <div className="card-details">
          <h3 className="item-name">{item.itemName || 'Jewelry Item'}</h3>
          
          <div className="tag-status-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div className="tag-badge" style={{ marginBottom: 0 }}>Tag: {item.tagId}</div>
            {item.status === 'Available' && <span className="status-dot green-dot" title="Available"></span>}
            {(item.status === 'Sold' || item.status === 'Estimated') && <span className="status-dot red-dot" title="Sold"></span>}
          </div>
                             
          <div className="specs-row">
            <span><b>Wt:</b> {item.weight}g</span>
            <span><b>Purity:</b> {item.purity}</span>
          </div>
          
          {item.remarks && <p className="item-remarks">{item.remarks}</p>}
          
          {/* Conditional Action Button */}
          {!isStaffUser(userRole) ? (
            <button 
              className="upload-btn" 
              onClick={() => openEnquiryModal(item.tagId, item.itemName)}
              style={{ backgroundColor: '#2874f0', color: '#fff', borderColor: '#2874f0' }}
            >
              🛒 Place My Enquiry
            </button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <button className="upload-btn" onClick={() => openUploadModal(item.tagId)}>
                {item.imageUrl ? '🔄 Add/Change Photos' : '📸 Upload Photos'}
              </button>
              <button 
                className="upload-btn" 
                disabled={item.status !== 'Available'}
                style={{ 
                  background: item.status !== 'Available' ? '#6c757d' : '#28a745', 
                  color: '#fff',
                  opacity: item.status !== 'Available' ? 0.65 : 1,
                  cursor: item.status !== 'Available' ? 'not-allowed' : 'pointer'
                }} 
                onClick={() => navigate('/sales', { state: { autoFetchTag: item.tagId } })}>
                ➕ Add to Sales
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="collection-container">
      
      {/* ================= TOP LIVE RATE BAR (Collapsible with 10s Auto-Hide) ================= */}
      {showLiveRates && (
        <div className="collection-live-rates-container">
          <div className="live-rate-header-control">
            <div className="live-rate-timer-badge">
              <span className="live-rate-pulse-dot"></span>
              <span>Today's Live Rates • Auto-closing in <b>{rateCountdown}s</b></span>
            </div>
            <button 
              type="button" 
              className="live-rate-close-icon-btn" 
              onClick={handleCloseLiveRates}
              title="Close Live Rates Now"
            >
              ×
            </button>
          </div>
          <LiveRateStrip onEditingChange={handleRatesEditingChange} />
        </div>
      )}

      {/* 1. Offer Banner */}
      {isOfferActive && offerText && (
        <div style={{
          background: 'linear-gradient(135deg, #ff4d4f, #ff7875)',
          color: '#fff',
          textAlign: 'center',
          padding: '12px 20px',
          fontSize: '16px',
          fontWeight: 'bold',
          borderRadius: '8px',
          marginBottom: '15px',
          boxShadow: '0 4px 10px rgba(255, 77, 79, 0.2)'
        }}>
          🔥 {offerText} 🔥
        </div>
      )}

      {/* 🛠️ 2. Manage Offer Banner Button (Non-Guest Users) */}
      {isStaffUser(userRole) && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
          <button 
            onClick={() => setIsEditingOffer(!isEditingOffer)}
            style={{
              background: '#212121',
              color: '#fff',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold'
            }}
          >
            {isEditingOffer ? '⚙️ Close Control Panel' : '⚙️ Manage Offer Banner'}
          </button>
        </div>
      )}

      {/* 📝 3. Offer Control Panel (Non-Guest Users) */}
      {isStaffUser(userRole) && isEditingOffer && (
        <div style={{
          background: '#fff',
          padding: '15px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          marginBottom: '20px',
          border: '1px solid #e0e0e0'
        }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#222' }}>⚙️ Operator Offer Control Panel</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#555' }}>
              Offer Message Text:
              <input 
                type="text" 
                value={inputOfferText} 
                onChange={(e) => setInputOfferText(e.target.value)} 
                placeholder="Ex: Diwali Special Offer!"
                style={{ width: '100%', padding: '8px', marginTop: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 'bold', color: '#555', cursor: 'pointer' }}>
              <input 
                type="checkbox" 
                checked={isOfferActive} 
                onChange={(e) => setIsOfferActive(e.target.checked)} 
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              Turn On/OFF Offer Banner
            </label>
            <button 
              onClick={handleUpdateOffer}
              style={{ background: '#2874f0', color: '#fff', border: 'none', padding: '8px 15px', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer', alignSelf: 'flex-start' }}
            >
              🚀 Save & Go Live
            </button>
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="filter-group">
          <label>Metal:</label>
          <select value={metal} onChange={(e) => handleMetalChange(e.target.value)}>
            <option value="all">All Metals</option>
            {masterMetals.map((m, index) => (
              <option key={index} value={m}>{m}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Purity:</label>
          <select value={purity} onChange={(e) => setPurity(e.target.value)}>
            <option value="all">All Purities</option>
            {masterPurities.map((p, index) => (
              <option key={index} value={p}>{p}</option>
            ))}
          </select>
        </div>

        {/* 💍 Groups Dropdown: After Purity, Before Sort By */}
        <div className="filter-group">
          <label>Groups:</label>
          <select value={group} onChange={(e) => setGroup(e.target.value)}>
            <option value="all">All Groups</option>
            {masterGroups.map((g, index) => (
              <option key={index} value={g}>{g}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Sort:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="latest">Latest First</option>
            <option value="oldest">Oldest First</option>
            <option value="lowToHigh">Weight: Lighter to Higher</option>
            <option value="highToLow">Weight: Higher to Lower</option>
          </select>
        </div>

        {/* 👑 Admin Mode Live Summary */}
        {isStaffUser(userRole) && (
          <div className="admin-stock-summary">
            👑 Admin Mode: {stockData.length} Total Cards ({stockData.filter(i => String(i.flag) === '1').length} Live for Guests)
          </div>
        )}
      </div>

      {/* Main Grid / Grouped Sections */}
      {loading ? (
        <div className="loading-text">Bhai, maal load ho raha hai...</div>
      ) : finalSortedStock.length === 0 ? (
        <div className="no-data">Bhai, is filter me koi maal nahi mila!</div>
      ) : (
        <div className="collection-groups-container">
          {Object.entries(groupedStock).map(([groupTitle, items]) => (
            <section key={groupTitle} className="collection-group-section">
              <div className="collection-group-header">
                <div className="group-header-title-box">
                  <span className="group-header-icon">✨</span>
                  <h2 className="group-header-title">{groupTitle}</h2>
                  <span className="group-header-count">({items.length} {items.length === 1 ? 'Design' : 'Designs'})</span>
                </div>
                <div className="group-header-line"></div>
              </div>

              <div className="products-grid">
                {items.map((item) => renderProductCard(item))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Upload Modal (Non-Guest Users) */}
      {isModalOpen && isStaffUser(userRole) && (
        <div className="tx-modal-overlay">
          <div className="tx-modal-content">
            <h3>📸 Tag ID: {selectedTag} ke liye Photos Upload</h3>
            <p className="modal-help-text">Aap ek sath 4-6 alag angles ki photos select kar sakte hain.</p>
            
            <form onSubmit={handleUploadSubmit}>
              <div className="file-input-group">
                <input type="file" multiple accept="image/*" onChange={handleFileChange} id="multi-file-picker" />
                <label htmlFor="multi-file-picker" className="custom-file-label">
                  📁 Select Images ({selectedFiles.length} selected)
                </label>
              </div>
              <div className="preview-selected-container">
                {selectedFiles.map((file, idx) => (
                  <div className="preview-selected-badge" key={idx}>{file.name}</div>
                ))}
              </div>
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setIsModalOpen(false)}>Cancel</button>
                <button type="submit" className="submit-btn" disabled={uploading}>
                  {uploading ? 'Uploading...' : '🚀 Start Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Customer Enquiry Modal (Guest Users) */}
      {isEnquiryModalOpen && (
        <div className="tx-modal-overlay">
          <div className="tx-modal-content" style={{ maxWidth: '400px' }}>
            <h3 style={{ borderBottom: '2px solid #2874f0', paddingBottom: '10px', color: '#2874f0' }}>
              🛒 Item Enquiry Form
            </h3>
            
            <div style={{ backgroundColor: '#8a7ef5', padding: '10px', borderRadius: '6px', marginBottom: '15px', fontSize: '14px' }}>
              <div><b>Tag ID:</b> {enquiryItem.tagId}</div>
              <div style={{ marginTop: '4px' }}><b>Item Name:</b> {enquiryItem.itemName}</div>
            </div>

            <form onSubmit={handleEnquirySubmit}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', fontSize: '13px', marginBottom: '4px', color: '#8a7ef5' }}>Your Full Name *</label>
                <input 
                  type="text" 
                  required
                  placeholder="Enter your name"
                  value={enquiryForm.customerName}
                  onChange={(e) => setEnquiryForm({...enquiryForm, customerName: e.target.value})}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', fontSize: '13px', marginBottom: '4px', color: '#8a7ef5' }}>Mobile Number *</label>
                <input 
                  type="tel" 
                  required
                  pattern="[0-9]{10}"
                  placeholder="Enter 10-digit mobile number"
                  value={enquiryForm.customerMobile}
                  onChange={(e) => setEnquiryForm({...enquiryForm, customerMobile: e.target.value})}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', fontSize: '13px', marginBottom: '4px', color: '#8a7ef5' }}>Your Remarks / Message (Optional)</label>
                <textarea 
                  rows="3"
                  placeholder="Ask about price, weight customization etc..."
                  value={enquiryForm.customerRemarks}
                  onChange={(e) => setEnquiryForm({...enquiryForm, customerRemarks: e.target.value})}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box', resize: 'none' }}
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setIsEnquiryModalOpen(false)}>Close</button>
                <button type="submit" className="submit-btn" disabled={submittingEnquiry} style={{ backgroundColor: '#2874f0' }}>
                  {submittingEnquiry ? 'Submitting...' : '🚀 Submit Enquiry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 🔍 Lightbox Photo Zoom Modal with Loop Navigation */}
      {zoomModal.isOpen && (
        <div 
          className="lightbox-overlay" 
          onClick={handleCloseZoom}
        >
          {/* Top Header Bar: Tag ID, Item Name, Image Counter (e.g. 1 / 4), Close Button */}
          <div className="lightbox-header" onClick={(e) => e.stopPropagation()}>
            <div className="lightbox-title-box">
              {zoomModal.tagId && <span className="lightbox-tag-badge">Tag: {zoomModal.tagId}</span>}
              {zoomModal.itemName && <span className="lightbox-item-name">{zoomModal.itemName}</span>}
            </div>

            <div className="lightbox-counter-badge">
              {zoomModal.currentIndex + 1} / {zoomModal.images.length}
            </div>

            <button 
              type="button" 
              className="lightbox-close-btn" 
              onClick={handleCloseZoom}
              aria-label="Close"
              title="Close (Esc)"
            >
              ×
            </button>
          </div>

          {/* Middle Body: Left Nav Arrow (<), Big Image with Touch Swipe, Right Nav Arrow (>) */}
          <div 
            className="lightbox-body"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {zoomModal.images.length > 1 && (
              <button 
                type="button"
                className="lightbox-nav-btn prev-btn" 
                onClick={handlePrevImage}
                aria-label="Previous Image"
                title="Previous Image (Left Arrow Key / Swipe Right)"
              >
                &#10094;
              </button>
            )}

            <div 
              className="lightbox-img-wrapper" 
              onClick={(e) => e.stopPropagation()}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <img 
                key={zoomModal.images[zoomModal.currentIndex]}
                src={zoomModal.images[zoomModal.currentIndex]} 
                alt={zoomModal.itemName || 'Zoomed Item'} 
                className="lightbox-zoomed-img" 
                draggable="false"
              />
            </div>

            {zoomModal.images.length > 1 && (
              <button 
                type="button"
                className="lightbox-nav-btn next-btn" 
                onClick={handleNextImage}
                aria-label="Next Image"
                title="Next Image (Right Arrow Key / Swipe Left)"
              >
                &#10095;
              </button>
            )}
          </div>

          {/* Bottom Thumbnails Strip (when product has multiple angles) */}
          {zoomModal.images.length > 1 && (
            <div className="lightbox-thumbnails" onClick={(e) => e.stopPropagation()}>
              {zoomModal.images.map((url, idx) => (
                <div 
                  key={idx} 
                  className={`lightbox-thumb-item ${zoomModal.currentIndex === idx ? 'active' : ''}`}
                  onClick={(e) => handleSelectZoomImage(idx, e)}
                  title={`View Angle ${idx + 1}`}
                >
                  <img src={url} alt={`angle-${idx + 1}`} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default Collection;